import { useEffect, useRef } from "react";
import { useApi } from "./api";

export const PRESENCE_BEAT_INTERVAL_MS = 60 * 1000;

// Disabled for the closed beta. The 60s heartbeat writes one KV record per
// active tab per minute (~1,440/day/user), which alone exceeds the Workers
// Free plan's ~1,000 writes/day cap. Flip to `true` once on Workers Paid to
// restore the admin "who's online" view. See spec
// docs/superpowers/specs/2026-05-31-production-cutover-kv-rekey-design.md §11.4.
export const PRESENCE_ENABLED = false;

export function presenceBeatUrl(): string {
  return "/api/presence/beat";
}

interface BeatLocation {
  courseSlug?: string;
  chapter?: string;
  lesson?: string;
}

export interface UsePresenceHeartbeatOptions {
  // Optional location to attach to each beat. Plan 09 admin reads this to
  // show "where the student is in the course". Plan 08 callers can omit it.
  location?: BeatLocation;
}

function isVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export function usePresenceHeartbeat(opts: UsePresenceHeartbeatOptions = {}): void {
  const { apiFetch } = useApi();
  const locationRef = useRef<BeatLocation | undefined>(opts.location);
  useEffect(() => {
    locationRef.current = opts.location;
  }, [opts.location]);

  useEffect(() => {
    if (!PRESENCE_ENABLED) return;
    const beat = (): void => {
      if (!isVisible()) return;
      void apiFetch(presenceBeatUrl(), {
        method: "POST",
        body: JSON.stringify(locationRef.current ?? {})
      }).catch(() => {
        // Silently swallow — presence is best-effort.
      });
    };
    beat();
    const interval = setInterval(beat, PRESENCE_BEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
